import type { LayoutBlock } from '@web-studio/shared-schemas';
import { REGISTRY_BLOCK } from '@web-studio/shared-schemas';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../lib/imageProxy';
import { SITE_IMAGE_SIZES } from '../lib/responsiveImageSizes';
import { pickBlockField } from '../lib/localized';
import { parseMenuColumns, type MenuItem } from '../lib/parseMenuColumns';
import { pickChrome } from '../lib/siteChromeCopy';
import { escapeHtml } from './escape';
import {
  renderMenuItemDietaryTagsHtml,
  renderMenuItemNameHtml,
} from './menuItemMarksHtml';
import type { BlockHtmlRenderContext } from './renderSiteHtml';

type MenuHtmlRenderer = (block: LayoutBlock, ctx: BlockHtmlRenderContext) => string;

const STORY_NAME = { showLegalMarks: false, showOrderCode: false } as const;
const STORY_DIETARY = { iconsOnly: true, placement: 'inline' as const };

function itemImageUrl(
  block: LayoutBlock,
  item: MenuItem,
  columnIndex: number,
  itemIndex: number,
): string | undefined {
  const fromItem = item.imageUrl?.trim();
  if (fromItem) {
    return fromItem;
  }
  const fromMedia = block.media[`itemImage_${columnIndex}_${itemIndex}`];
  return typeof fromMedia === 'string' && fromMedia.length > 0 ? fromMedia : undefined;
}

function listDishes(block: LayoutBlock, locale: string) {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionSubtitle = pickBlockField(block.content, 'sectionSubtitle', locale);
  const columns = parseMenuColumns(pickBlockField(block.content, 'menuData', locale));
  const dishes: Array<{ item: MenuItem; columnIndex: number; itemIndex: number }> = [];
  columns.forEach((col, columnIndex) => {
    (col.items ?? []).forEach((item, itemIndex) => {
      if (item.name?.trim() || item.price?.trim() || item.description?.trim()) {
        dishes.push({ item, columnIndex, itemIndex });
      }
    });
  });
  return { sectionTitle, sectionSubtitle, dishes };
}

function sectionShell(
  block: LayoutBlock,
  className: string,
  inner: string,
): string {
  return `<section class="${className}" data-block-id="${escapeHtml(block.blockId)}" data-component="${escapeHtml(block.componentType)}" data-registry-key="${escapeHtml(block.registryBlockKey ?? '')}" id="menu">${inner}</section>`;
}

export function renderMenuHighlightMarquee(block: LayoutBlock, ctx: BlockHtmlRenderContext): string {
  const { locale } = ctx;
  const { sectionTitle, sectionSubtitle, dishes } = listDishes(block, locale);
  /* Repeat until ≥16 chips so short lists still fill the band (matches React expandMarqueeChips). */
  const cycles = dishes.length === 0 ? 0 : Math.max(2, Math.ceil(16 / dishes.length));
  const expanded = Array.from({ length: cycles }, () => dishes).flat();
  const chips = expanded
    .map(({ item }, index) => {
      const cycle = dishes.length > 0 ? Math.floor(index / dishes.length) : 0;
      const desc = item.description
        ? `<span class="ws-hl-marquee__desc">${escapeHtml(item.description)}</span>`
        : '';
      return `<li class="ws-hl-marquee__chip" data-cycle="${cycle}"><span class="ws-hl-marquee__name">${escapeHtml(item.name)}</span>${desc}</li>`;
    })
    .join('');
  const group = `<ul class="ws-hl-marquee__group">${chips}</ul>`;
  const groupDup = `<ul class="ws-hl-marquee__group" aria-hidden="true">${chips}</ul>`;
  return sectionShell(
    block,
    'ws-hl-marquee',
    `<div class="ws-hl-marquee__inner"><header class="ws-hl-marquee__intro">${sectionTitle ? `<h2 class="ws-hl-marquee__title">${escapeHtml(sectionTitle)}</h2>` : ''}${sectionSubtitle ? `<p class="ws-hl-marquee__subtitle">${escapeHtml(sectionSubtitle)}</p>` : ''}</header>${dishes.length > 0 ? `<div class="ws-hl-marquee__viewport"><div class="ws-hl-marquee__track" aria-label="${escapeHtml(sectionTitle || 'Highlights')}">${group}${groupDup}</div></div>` : ''}</div>`,
  );
}

export function renderMenuHighlightHeroPlate(
  block: LayoutBlock,
  ctx: BlockHtmlRenderContext,
): string {
  const { locale } = ctx;
  const { sectionTitle, sectionSubtitle, dishes } = listDishes(block, locale);
  const plates = dishes.slice(0, 4);
  const interactive = plates.length > 1;
  const chipsNavLabel = pickChrome('highlightHeroPlateNav', locale);
  const goToDishLabel = pickChrome('highlightHeroPlateGoTo', locale);

  const leadHtml = plates
    .map(({ item, columnIndex, itemIndex }, index) => {
      const imageUrl = itemImageUrl(block, item, columnIndex, itemIndex);
      const imageSrc = imageUrl ? buildSiteImageProxyUrl(imageUrl, 1200, 78) : '';
      const imageSrcSet = imageUrl
        ? buildSiteImageSrcSet(imageUrl, [480, 720, 960, 1200], 78)
        : '';
      /* Eager for all plates (≤4) so chip swaps never flash an undecoded image. */
      const media = imageUrl
        ? `<img class="ws-hl-hero__media-frame" src="${escapeHtml(imageSrc)}" srcset="${escapeHtml(imageSrcSet)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.menuHighlightHero)}" alt="${escapeHtml(item.name || 'Dish')}" width="1200" height="900" loading="eager" decoding="async" />`
        : '<div class="ws-hl-hero__media-fallback" aria-hidden="true"></div>';
      const desc = item.description
        ? `<p class="ws-hl-hero__desc">${escapeHtml(item.description)}</p>`
        : '';
      const dietary = renderMenuItemDietaryTagsHtml(item, locale, STORY_DIETARY);
      const activeClass =
        !interactive || index === 0 ? ' ws-hl-hero__lead--active' : '';
      const panelRole = interactive
        ? ` role="tabpanel" aria-labelledby="ws-hl-hero-tab-${escapeHtml(block.blockId)}-${index}"`
        : '';
      const hiddenAttr = interactive && index !== 0 ? ' aria-hidden="true"' : '';
      const indexAttr = interactive ? ` data-hero-index="${index}"` : '';
      return `<article class="ws-hl-hero__lead${activeClass}"${indexAttr}${panelRole}${hiddenAttr}><div class="ws-hl-hero__media">${media}</div><div class="ws-hl-hero__body"><div class="ws-hl-hero__head">${renderMenuItemNameHtml(item, locale, { nameClass: 'ws-hl-hero__name', ...STORY_NAME })}${dietary}</div>${desc}</div></article>`;
    })
    .join('');

  const stageHtml = interactive
    ? `<div class="ws-hl-hero__stage">${leadHtml}</div>`
    : leadHtml;

  const chips = interactive
    ? `<div class="ws-hl-hero__chips" role="tablist" aria-label="${escapeHtml(chipsNavLabel || sectionTitle || 'Highlights')}">${plates
        .map(({ item }, index) => {
          const desc = item.description
            ? `<span class="ws-hl-hero__chip-desc">${escapeHtml(item.description)}</span>`
            : '';
          const selected = index === 0;
          const label = item.name?.trim()
            ? item.name
            : goToDishLabel.replace('{n}', String(index + 1));
          return `<button type="button" role="tab" id="ws-hl-hero-tab-${escapeHtml(block.blockId)}-${index}" class="ws-hl-hero__chip" data-hero-index="${index}" aria-selected="${selected ? 'true' : 'false'}" aria-label="${escapeHtml(label)}" tabindex="${selected ? '0' : '-1'}"><span class="ws-hl-hero__chip-name">${escapeHtml(item.name)}</span>${desc}</button>`;
        })
        .join('')}</div>`
    : '';

  const sectionAttrs = interactive
    ? ' data-ws-hl-hero data-active="0"'
    : '';
  return `<section class="ws-hl-hero"${sectionAttrs} data-block-id="${escapeHtml(block.blockId)}" data-component="${escapeHtml(block.componentType)}" data-registry-key="${escapeHtml(block.registryBlockKey ?? '')}" id="menu"><div class="ws-hl-hero__inner"><header class="ws-hl-hero__intro"><span class="ws-hl-hero__accent" aria-hidden="true"></span>${sectionTitle ? `<h2 class="ws-hl-hero__title">${escapeHtml(sectionTitle)}</h2>` : ''}${sectionSubtitle ? `<p class="ws-hl-hero__subtitle">${escapeHtml(sectionSubtitle)}</p>` : ''}</header>${stageHtml}${chips}</div></section>`;
}

export function renderMenuHighlightZine(block: LayoutBlock, ctx: BlockHtmlRenderContext): string {
  const { locale } = ctx;
  const { sectionTitle, sectionSubtitle, dishes } = listDishes(block, locale);
  const stack = dishes.slice(0, 5);
  const interactive = stack.length > 1;
  const cards = stack
    .map(({ item, columnIndex, itemIndex }, index) => {
      const imageUrl = itemImageUrl(block, item, columnIndex, itemIndex);
      const imageSrc = imageUrl ? buildSiteImageProxyUrl(imageUrl, 720, 78) : '';
      const imageSrcSet = imageUrl
        ? buildSiteImageSrcSet(imageUrl, [320, 480, 640, 900], 78)
        : '';
      const media = imageUrl
        ? `<img class="ws-hl-zine__media-frame" src="${escapeHtml(imageSrc)}" srcset="${escapeHtml(imageSrcSet)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.menuHighlightZine)}" alt="${escapeHtml(item.name || 'Dish')}" width="720" height="720" loading="${index === 0 ? 'eager' : 'lazy'}" decoding="async" />`
        : '<div class="ws-hl-zine__media-fallback" aria-hidden="true"></div>';
      const frontClass = index === 0 ? ' ws-hl-zine__card--front' : '';
      const hiddenAttr = interactive && index !== 0 ? ' aria-hidden="true"' : '';
      const nameHtml = renderMenuItemNameHtml(item, locale, {
        nameClass: 'ws-hl-zine__name',
        ...STORY_NAME,
      });
      const descHtml = `<p class="ws-hl-zine__desc">${item.description ? escapeHtml(item.description) : ''}</p>`;
      const dietary = renderMenuItemDietaryTagsHtml(item, locale, STORY_DIETARY);
      return `<article class="ws-hl-zine__card${frontClass}" role="listitem" data-zine-index="${index}" style="--ws-hl-zine-i: ${index}"${hiddenAttr}><div class="ws-hl-zine__media">${media}</div><div class="ws-hl-zine__caption"><div class="ws-hl-zine__head">${nameHtml}${dietary}</div>${descHtml}</div></article>`;
    })
    .join('');
  const fanDepth = Math.max(0, stack.length - 1);
  const stackAttrs = interactive
    ? ` role="list" data-ws-hl-zine data-interactive="true" data-front="0" tabindex="0" style="--ws-hl-zine-depth: ${fanDepth}"`
    : ` role="list" style="--ws-hl-zine-depth: ${fanDepth}"`;
  const marksNavLabel = pickChrome('highlightZineNav', locale);
  const goToCardLabel = pickChrome('highlightZineGoTo', locale);
  const marks = interactive
    ? `<nav class="ws-hl-zine__marks" aria-label="${escapeHtml(marksNavLabel)}">${stack
        .map(
          (_item, index) =>
            `<button type="button" class="ws-hl-zine__mark" aria-label="${escapeHtml(goToCardLabel.replace('{n}', String(index + 1)))}"${index === 0 ? ' aria-current="true"' : ''}></button>`,
        )
        .join('')}</nav>`
    : '';
  return sectionShell(
    block,
    'ws-hl-zine',
    `<div class="ws-hl-zine__inner"><header class="ws-hl-zine__intro">${sectionTitle ? `<h2 class="ws-hl-zine__title">${escapeHtml(sectionTitle)}</h2>` : ''}${sectionSubtitle ? `<p class="ws-hl-zine__subtitle">${escapeHtml(sectionSubtitle)}</p>` : ''}</header><div class="ws-hl-zine__stage"><div class="ws-hl-zine__stack"${stackAttrs}><div class="ws-hl-zine__fan">${cards}</div></div>${marks}</div></div>`,
  );
}

export function renderMenuHighlightChalkboard(
  block: LayoutBlock,
  ctx: BlockHtmlRenderContext,
): string {
  const { locale } = ctx;
  const { sectionTitle, sectionSubtitle, dishes } = listDishes(block, locale);
  const rows = dishes
    .map(({ item }) => {
      const desc = item.description
        ? `<p class="ws-hl-chalk__desc">${escapeHtml(item.description)}</p>`
        : '';
      return `<li class="ws-hl-chalk__row"><span class="ws-hl-chalk__name">${escapeHtml(item.name)}</span>${desc}</li>`;
    })
    .join('');
  return sectionShell(
    block,
    'ws-hl-chalk',
    `<div class="ws-hl-chalk__board"><header class="ws-hl-chalk__intro">${sectionTitle ? `<h2 class="ws-hl-chalk__title">${escapeHtml(sectionTitle)}</h2>` : ''}${sectionSubtitle ? `<p class="ws-hl-chalk__subtitle">${escapeHtml(sectionSubtitle)}</p>` : ''}</header><ul class="ws-hl-chalk__list">${rows}</ul></div>`,
  );
}

export function renderMenuHighlightChefsCut(
  block: LayoutBlock,
  ctx: BlockHtmlRenderContext,
): string {
  const { locale } = ctx;
  const { sectionTitle, sectionSubtitle, dishes } = listDishes(block, locale);
  const items = dishes
    .slice(0, 5)
    .map(({ item }, index) => {
      const num = String(index + 1).padStart(2, '0');
      const desc = item.description
        ? `<p class="ws-hl-chefs__desc">${escapeHtml(item.description)}</p>`
        : '';
      const dietary = renderMenuItemDietaryTagsHtml(item, locale, STORY_DIETARY);
      return `<li class="ws-hl-chefs__item"><span class="ws-hl-chefs__num" aria-hidden="true">${num}</span><div class="ws-hl-chefs__body"><div class="ws-hl-chefs__head">${renderMenuItemNameHtml(item, locale, { nameClass: 'ws-hl-chefs__name', ...STORY_NAME })}${dietary}</div>${desc}</div></li>`;
    })
    .join('');
  return sectionShell(
    block,
    'ws-hl-chefs',
    `<div class="ws-hl-chefs__inner"><header class="ws-hl-chefs__intro">${sectionTitle ? `<h2 class="ws-hl-chefs__title">${escapeHtml(sectionTitle)}</h2>` : ''}${sectionSubtitle ? `<p class="ws-hl-chefs__subtitle">${escapeHtml(sectionSubtitle)}</p>` : ''}</header><ol class="ws-hl-chefs__list">${items}</ol></div>`,
  );
}

export function renderMenuHighlightTodaySplit(
  block: LayoutBlock,
  ctx: BlockHtmlRenderContext,
): string {
  const { locale } = ctx;
  const { sectionTitle, sectionSubtitle, dishes } = listDishes(block, locale);
  const list = dishes.slice(0, 4);
  const wash = list[0];
  const washUrl = wash ? itemImageUrl(block, wash.item, wash.columnIndex, wash.itemIndex) : undefined;
  const washSrc = washUrl ? buildSiteImageProxyUrl(washUrl, 1200, 55) : '';
  const washSrcSet = washUrl ? buildSiteImageSrcSet(washUrl, [640, 960, 1200], 55) : '';
  const washHtml = washUrl
    ? `<div class="ws-hl-today__wash" aria-hidden="true"><img class="ws-hl-today__wash-img" src="${escapeHtml(washSrc)}" srcset="${escapeHtml(washSrcSet)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.menuHighlightToday)}" alt="" width="1200" height="800" loading="eager" decoding="async" /></div>`
    : '<div class="ws-hl-today__wash ws-hl-today__wash--tone" aria-hidden="true"></div>';
  const rows = list
    .map(({ item }) => {
      const desc = item.description
        ? `<p class="ws-hl-today__desc">${escapeHtml(item.description)}</p>`
        : '';
      const dietary = renderMenuItemDietaryTagsHtml(item, locale, STORY_DIETARY);
      return `<li class="ws-hl-today__row"><div class="ws-hl-today__story"><div class="ws-hl-today__head">${renderMenuItemNameHtml(item, locale, { nameClass: 'ws-hl-today__name', ...STORY_NAME })}${dietary}</div>${desc}</div></li>`;
    })
    .join('');
  return sectionShell(
    block,
    'ws-hl-today',
    `<div class="ws-hl-today__panel">${washHtml}<div class="ws-hl-today__content"><aside class="ws-hl-today__kicker">${sectionTitle ? `<h2 class="ws-hl-today__title">${escapeHtml(sectionTitle)}</h2>` : ''}${sectionSubtitle ? `<p class="ws-hl-today__subtitle">${escapeHtml(sectionSubtitle)}</p>` : ''}</aside><ul class="ws-hl-today__list">${rows}</ul></div></div>`,
  );
}

export function renderMenuHighlightPriceTiles(
  block: LayoutBlock,
  ctx: BlockHtmlRenderContext,
): string {
  const { locale } = ctx;
  const { sectionTitle, sectionSubtitle, dishes } = listDishes(block, locale);
  const tiles = dishes
    .map(({ item }) => {
      const desc = item.description
        ? `<p class="ws-hl-price__desc">${escapeHtml(item.description)}</p>`
        : '';
      const dietary = renderMenuItemDietaryTagsHtml(item, locale, STORY_DIETARY);
      return `<li class="ws-hl-price__tile"><div class="ws-hl-price__head"><p class="ws-hl-price__name">${escapeHtml(item.name)}</p>${dietary}</div>${desc}</li>`;
    })
    .join('');
  return sectionShell(
    block,
    'ws-hl-price',
    `<div class="ws-hl-price__inner"><header class="ws-hl-price__intro">${sectionTitle ? `<h2 class="ws-hl-price__title">${escapeHtml(sectionTitle)}</h2>` : ''}${sectionSubtitle ? `<p class="ws-hl-price__subtitle">${escapeHtml(sectionSubtitle)}</p>` : ''}</header><ul class="ws-hl-price__grid">${tiles}</ul></div>`,
  );
}

export function renderMenuHighlightDishCards(
  block: LayoutBlock,
  ctx: BlockHtmlRenderContext,
): string {
  const { locale } = ctx;
  const { sectionTitle, sectionSubtitle, dishes } = listDishes(block, locale);
  const cards = dishes
    .map(({ item, columnIndex, itemIndex }) => {
      const imageUrl = itemImageUrl(block, item, columnIndex, itemIndex);
      const imageSrc = imageUrl ? buildSiteImageProxyUrl(imageUrl, 720, 78) : '';
      const imageSrcSet = imageUrl
        ? buildSiteImageSrcSet(imageUrl, [320, 480, 640, 900], 78)
        : '';
      const media = imageUrl
        ? `<img class="ws-hl-dishes__media-frame" src="${escapeHtml(imageSrc)}" srcset="${escapeHtml(imageSrcSet)}" sizes="${escapeHtml(SITE_IMAGE_SIZES.menuDishCard)}" alt="${escapeHtml(item.name || 'Dish')}" width="720" height="720" loading="lazy" decoding="async" />`
        : '<div class="ws-hl-dishes__media-placeholder" aria-hidden="true"></div>';
      const nameHtml = renderMenuItemNameHtml(item, locale, {
        nameClass: 'ws-hl-dishes__name',
        ...STORY_NAME,
      });
      const descHtml = item.description
        ? `<p class="ws-hl-dishes__desc">${escapeHtml(item.description)}</p>`
        : '';
      const dietary = renderMenuItemDietaryTagsHtml(item, locale, STORY_DIETARY);
      return `<li><article class="ws-hl-dishes__card"><div class="ws-hl-dishes__media">${media}</div><div class="ws-hl-dishes__body"><div class="ws-hl-dishes__head">${nameHtml}${dietary}</div>${descHtml}</div></article></li>`;
    })
    .join('');
  return sectionShell(
    block,
    'ws-hl-dishes',
    `<div class="ws-hl-dishes__inner"><header class="ws-hl-dishes__intro">${sectionTitle ? `<h2 class="ws-hl-dishes__title">${escapeHtml(sectionTitle)}</h2>` : ''}${sectionSubtitle ? `<p class="ws-hl-dishes__subtitle">${escapeHtml(sectionSubtitle)}</p>` : ''}</header>${dishes.length > 0 ? `<ul class="ws-hl-dishes__grid">${cards}</ul>` : ''}</div>`,
  );
}

const P = REGISTRY_BLOCK.presentation;

export const HIGHLIGHT_MENU_SSR_OVERRIDES: Partial<Record<string, MenuHtmlRenderer>> = {
  [P.staticMenuHighlightMarquee]: renderMenuHighlightMarquee,
  [P.staticMenuHighlightHeroPlate]: renderMenuHighlightHeroPlate,
  [P.staticMenuHighlightZine]: renderMenuHighlightZine,
  [P.staticMenuHighlightChalkboard]: renderMenuHighlightChalkboard,
  [P.staticMenuHighlightChefsCut]: renderMenuHighlightChefsCut,
  [P.staticMenuHighlightTodaySplit]: renderMenuHighlightTodaySplit,
  [P.staticMenuHighlightPriceTiles]: renderMenuHighlightPriceTiles,
  [P.staticMenuHighlightDishCards]: renderMenuHighlightDishCards,
};
